import { CssBaseline } from "@mui/material";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import React, { ReactNode, createContext, useContext, useEffect, useState } from "react";
import { PaletteMode, createTheme } from "theme";
import {
  getAuthUserIdFromStorage,
  getBlogsUserPreferences,
  setBlogsUserPreferences,
} from "screens/Blogs/blogsUserPreferences";

interface ThemeContextType {
  mode: PaletteMode;
  toggleTheme: () => void;
  setMode: (mode: PaletteMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within a ThemeContextProvider");
  }
  return context;
};

interface ThemeContextProviderProps {
  children: ReactNode;
  initialMode?: PaletteMode;
}

export const ThemeContextProvider: React.FC<ThemeContextProviderProps> = ({
  children,
  initialMode = "light",
}) => {
  const [mode, setModeState] = useState<PaletteMode>(() => {
    // Logged-in user: use their saved theme from blogs prefs
    const userId = getAuthUserIdFromStorage();
    if (userId) {
      const prefs = getBlogsUserPreferences(userId);
      if (prefs?.themeMode) return prefs.themeMode as PaletteMode;
    }
    // Guest or no saved prefs: use global themeMode
    const savedMode = localStorage.getItem("themeMode") as PaletteMode;
    return savedMode || initialMode;
  });

  useEffect(() => {
    const userId = getAuthUserIdFromStorage();
    if (userId) {
      setBlogsUserPreferences(userId, { themeMode: mode as "light" | "dark" });
    } else {
      localStorage.setItem("themeMode", mode);
    }
  }, [mode]);

  const setMode = (newMode: PaletteMode) => {
    setModeState(newMode);
  };

  const toggleTheme = () => {
    setModeState((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  const theme = createTheme({
    colorPreset: "schoolapp",
    contrast: "normal",
    direction: "ltr",
    paletteMode: mode,
    responsiveFontSizes: true,
  });

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, setMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
